import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createFlag,
  deleteFlag,
  getFlags,
  updateFlag,
  updateFlagEnvironment,
} from '../../api/flags';
import { useEnvironment } from '../useEnvironment/useEnvironment';
import type { IFlagEnvironmentInput, IFlagInput } from '../../types';

export const useFlags = () => {
  const { environment } = useEnvironment();
  return useQuery({
    queryKey: ['flags', environment],
    queryFn: () => getFlags(environment),
    select: ({ data }) => data,
    staleTime: 30_000,
  });
};

export const useFlagMutations = () => {
  const { environment } = useEnvironment();
  const client = useQueryClient();
  const refresh = () => client.invalidateQueries({ queryKey: ['flags', environment] });
  const create = useMutation({
    mutationFn: (input: IFlagInput) => createFlag(environment, input),
    onSuccess: refresh,
  });
  const update = useMutation({
    mutationFn: ({ id, input }: { id: string; input: IFlagInput }) =>
      updateFlag(environment, id, input),
    onSuccess: refresh,
  });
  const remove = useMutation({
    mutationFn: (id: string) => deleteFlag(environment, id),
    onSuccess: refresh,
  });
  const updateEnvironment = useMutation({
    mutationFn: ({
      flagId,
      flagEnvironmentId,
      input,
    }: {
      flagId: string;
      flagEnvironmentId: string;
      input: IFlagEnvironmentInput;
    }) => updateFlagEnvironment(environment, flagId, flagEnvironmentId, input),
    onSuccess: refresh,
  });
  return { create, update, remove, updateEnvironment };
};
